import { HilPackage } from '@cadstrom/backend/shared';
import { ComponentBoundarySchema } from '@cadstrom/sigma-engine-shared';
import { Editor } from '@monaco-editor/react';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import { useColorScheme } from '@mui/material/styles';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import isEqual from 'lodash.isequal';
import * as z from 'zod';
import { ZodJsonSchema } from '@cadstrom/cadstrom-shared';

import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

import { buildImplementationRulesOperatorPatch } from './implementation-rules-operator-patch.js';

import type { OnMount } from '@monaco-editor/react';
import type { json } from 'monaco-editor';

const fileMatch = 'UpdatePackageInputDto.json';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/raw/')({
	staticData: {
		navigation: {
			label: 'Package: Raw',
			keywords: ['raw', 'json'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') + 'Raw • Package Editor • Cadstrom',
			},
		],
	}),
	component: PackageEditorRawTab,
});

function PackageEditorRawTab() {
	const { colorScheme } = useColorScheme();
	const { packageFamilyId } = Route.useParams();
	const queryClient = useQueryClient();
	const { getPackageOptions, getFamilyPackagesOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);
				queryClient.setQueryData(getFamilyPackagesOptions.queryKey, (data) => ({
					pkgs: (data?.pkgs ?? []).map((p) => (p.id === res.pkg.id ? res.pkg : p)),
				}));

				setServerJson(stringify(res.pkg));
			},
		}),
	);

	const {
		draftState: draftJson,
		setDraftState: setDraftJson,
		setServerState: setServerJson,
		isDirty,
		isValid,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: stringify(pkg),
		mutationKeys: trpc.package.update.mutationKey(),
		isValid: (draftState) => {
			try {
				return HilPackage.schema.parse(JSON.parse(draftState));
			} catch {
				return false;
			}
		},
		saveFn: async (draftState, serverState, validationResult) => {
			const implementationRules = await buildImplementationRulesOperatorPatch(
				pkg.implementationRules,
				validationResult.implementationRules,
			);
			await updatePackage({
				id: pkg.id,
				data: {
					...(validationResult.manufacturerPartNumber !== pkg.manufacturerPartNumber ?
						{ manufacturerPartNumber: validationResult.manufacturerPartNumber }
					:	{}),
					fileId: validationResult.fileId,
					sigmaModelType: validationResult.sigmaModelType,
					signals: { set: validationResult.signals },
					differentialPairs: { set: validationResult.differentialPairs },
					pinMap: { set: validationResult.pinMap },
					sigmaModelParameters: { set: validationResult.sigmaModelParameters },
					absoluteMaximumRatings: validationResult.absoluteMaximumRatings,
					operatingCharacteristics: validationResult.operatingCharacteristics,
					functions: { set: validationResult.functions },
					resources: validationResult.resources,
					modes: validationResult.modes,
					boundaries: validationResult.boundaries,
					spiceModels: validationResult.spiceModels,
					subModelEditor: validationResult.subModelEditor,
					...(implementationRules === undefined ? {} : { implementationRules }),
				},
			});
		},
		equalityFn: (serverData, draftData) => {
			// Because of how the pin map data is stored in the backend, the backend doesn't make a distinction between
			// a nonexistent pin map entry and a pin map entry with an empty array. That breaks the equality check for
			// `isDirty`, so we need to filter out empty pin map entries to get it to work correctly.

			try {
				const pkgPinMapSchema = z
					.object({
						pinMap: z.record(z.string(), z.string().array()),
					})
					.passthrough();
				const parsedDraftData = pkgPinMapSchema.parse(JSON.parse(draftData));

				const draftDataPinMapWithoutEmpty = Object.fromEntries(
					Object.entries(parsedDraftData.pinMap).filter(([, pinNumbers]) => pinNumbers.length > 0),
				);

				const { datasheetUrl, ...parsedDraftDataWithoutDatasheetUrl } = parsedDraftData; // See comment in `stringify` function below for explanation of this line

				return isEqual(JSON.parse(serverData), {
					...parsedDraftDataWithoutDatasheetUrl,
					pinMap: draftDataPinMapWithoutEmpty,
				});
			} catch {
				return false;
			}
		},
	});

	const handleMount: OnMount = (editor, monaco) => {
		const existingSchemas =
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
			(monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas as json.DiagnosticsOptions['schemas']) ?? [];

		// TODO: Take this hacky schema out
		const newSchema = HilPackage.schema.extend({
			boundaries: ComponentBoundarySchema.array().optional(),
		});
		const schemas: json.DiagnosticsOptions['schemas'] = [
			// Filter out if we've already added this schema
			...existingSchemas.filter((schema) => !schema.fileMatch?.some((match) => match.includes(fileMatch))),

			{
				uri: `cadstrom://model/${fileMatch}`, // Make up a URI because Monaco needs one
				schema: ZodJsonSchema.fromSchema(newSchema),
				fileMatch: [`*/${fileMatch}`],
			},
		];

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- There's an error in the types of @monaco-editor/react
		monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			...monaco.languages.json.jsonDefaults.diagnosticsOptions,
			schemas,
		});
	};

	return (
		<div ref={saveAreaContainerRef} className='relative grow'>
			<Editor
				width='100%'
				height='100%'
				language='json'
				value={draftJson}
				theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
				options={{
					folding: true,
				}}
				onMount={handleMount}
				onChange={(value) => setDraftJson(value ?? '')}
				path={`${packageFamilyId}/${pkg.id}/${fileMatch}`}
			/>

			<Button
				disabled={!isValid || !isDirty}
				loading={updatePackageIsPending}
				startIcon={<SaveIcon />}
				className='absolute top-3 right-6 z-10'
				onClick={() => void handleSave()}
			>
				<p>Save</p>
			</Button>

			<p className='absolute right-0 bottom-0 z-10 bg-background px-1 py-0.5 text-xs text-gray-11 opacity-50 select-none'>
				F1 for editor options
			</p>
		</div>
	);
}

const stringify = (obj: object) => {
	// Since the value of `datasheetUrl` is retrieved at request-time based on `fileId` in the backend, the API will
	// return a different value for `datasheetUrl` (probably `null`) if the user updates `fileId`. This direct
	// relationship between `fileId` and `datasheetUrl` can mess with some of our logic in the editor, so it's easiest to
	// just omit `datasheetUrl` from the editor entirely. (It's currently a read-only property anyway.)

	return JSON.stringify(omit(obj, 'datasheetUrl'), undefined, '\t');
};
